import * as React from 'react';
import { memo, useCallback } from 'react';
import { FieldRenderProps, useField, useForm, useFormState } from 'react-final-form';
import { useTranslation } from 'react-i18next';

import { AdditionalTravellerFieldEnum, InputType, NameField, PassengerField, SubsidyField } from '../../types';
import { PassengerSwitch } from './PassengerSwitch/PassengerSwitch';
import Input from '../../../Input';
import { Select } from './Select/Select';
import { DateField } from './Date/Date';
import PhoneInput from '../../../PhoneInput';
import DocumentType from './DocumentType/DocumentType';
import LastName from './LastName/LastName';
import { fieldIsRequired, formatInvalidPassengerDate, getFieldRegExpMaxLength, validate } from '../../utils';
import DocExpiryDate from './DocExpiryDate/DocExpiryDate';
import { useMediaQuery } from 'react-responsive';
import { disableAutocompleteProps, MOBILE_MIN_WIDTH } from '../../../utils';
import { AviaPassengerType, DocTypes, TravellerFieldEnum, TravellerProfileFragment } from '@websky/graphql';
import { LinkedTraveller } from './LinkedTraveller/LinkedTraveller';
import Nationality from './Nationality/Nationality';
import { InputBaseComponentProps } from '@mui/material/InputBase';
import { MenuProps } from '@mui/material';
import { isNameFieldType } from '../../../PassengerForm/utils';
import PassengerSubsidySelect from './PassengerSubsidySelect';
import { FillValue } from '../../../Checkout/components/Checkout/Form/types';

export interface FieldProps {
	field: PassengerField;
	birthDateField?: PassengerField;
	passengerId: number;
	passengerType: AviaPassengerType;
	passengerDocType?: DocTypes;
	className?: string;
	onChange?: (name: string, value: string) => void;
	onTravellerSelect?: (values: TravellerProfileFragment, type?: TravellerFieldEnum, value?: string) => void;
	loadTravellers?: boolean;
	disabled?: boolean;
	selectedProfileId?: string;
	variant?: 'outlined' | 'filled' | 'standard';
	autofocus?: boolean;
	onSwap?: () => void;
	translateSelectOption?: boolean;
	menuProps?: Partial<MenuProps>;
	docExpiryDateField?: PassengerField;
}

export const Field: React.FunctionComponent<FieldProps> = memo(props => {
	const { t } = useTranslation('Passenger');

	const isMobile = useMediaQuery({ maxWidth: MOBILE_MIN_WIDTH });

	const isDocNumber = props.field.type === TravellerFieldEnum.DocNumber;
	const isBirthRegDocument = props.passengerDocType === DocTypes.BirthRegDocument;

	const escapedT = (label: string | void) =>
		typeof label !== 'undefined'
			? t(label, {
					// This is necessary to avoid colon problems in error messages.
					nsSeparator: '|',
					keySeparator: '>'
			  })
			: label;

	const fieldState = useField(props.field.name, {
		initialValue: props.field.defaultValue,
		type: props.field.input === InputType.Switch ? undefined : props.field.input,
		validate: props.disabled
			? () => {}
			: (value, allValues) => {
					const validatedData = validate(
						value,
						allValues['passengers']?.[props.passengerId] ?? {},
						props.field.input,
						props.field.validations,
						props.field.type,
						props.field.defaultValue,
						allValues['passengers']
					);

					if (props.field.type === TravellerFieldEnum.LinkedTraveller) {
						return validatedData;
					}

					return escapedT(validatedData);
			  },

		parse: isNameFieldType(props.field.type as TravellerFieldEnum) ? value => value ?? '' : undefined
	});

	const onChangeSelectHandler = useCallback(
		(value, event) => {
			props.onChange && props.onChange(props.field.name, value);
			(fieldState as FieldRenderProps<string, HTMLSelectElement>).input.onChange(event);
		},
		[props.onChange, fieldState]
	);

	const error =
		fieldState.meta.error &&
		(fieldState.meta.submitFailed ||
			(fieldState.meta.touched && fieldState.meta.modified) ||
			(fieldState.input.value && !fieldState.meta.modified))
			? fieldState.meta.error
			: fieldState.meta.submitError && !fieldState.meta.dirtySinceLastSubmit
			? fieldState.meta.submitError
			: '';

	const isRequired = props.field.validations && props.field.validations[0]?.required;
	const label = isRequired ? `${props.field.placeholder}*` : props.field.placeholder;

	const inputVariant = props.variant ? props.variant : isMobile ? 'standard' : 'filled';

	let inputMode: InputBaseComponentProps['inputMode'] = 'text';

	// Numeric input mode
	if (['LoyaltyNumber', 'LoyaltyAirline'].includes(props.field.type)) {
		inputMode = 'numeric';
	}

	const { mutators } = useForm();
	const { dirty } = useFormState();

	const onProfileDocumentSelectHandler = useCallback(
		(fillValue: FillValue) => {
			// поле даты действия документа скрыто, не нужно заполнять его в мутаторе
			// так как может быть невалидным а поскольку скрыто, пользователь не сможет исправить

			mutators.fillFromDocument(props.passengerId, fillValue, props.docExpiryDateField);
		},
		[props.passengerId, props.docExpiryDateField]
	);

	const clearFieldsHandler = () => {
		const mutator = mutators['clearFields'];
		mutator(props.passengerId);
	};

	if (props.field.input === InputType.Text) {
		if (props.field.type === TravellerFieldEnum.LinkedTraveller) {
			return (
				<LinkedTraveller
					value={fieldState.input.value}
					validations={props.field.validations}
					name={props.field.name}
					label={label}
					className={props.className}
					onChange={onChangeSelectHandler}
					onBlur={(fieldState as FieldRenderProps<string, HTMLTextAreaElement>).input.onBlur}
					error={t(fieldState.meta.error)}
					disabled={props.disabled}
					variant={inputVariant}
				/>
			);
		}

		const { values } = useFormState();

		const inputMaxLength = isDocNumber
			? getFieldRegExpMaxLength(values['passengers'][props.passengerId], props.field.validations)
			: undefined;

		return (
			<Input
				label={label}
				value={fieldState.input.value}
				name={props.field.name}
				className={props.className}
				disabled={props.disabled}
				autoFocus={props.autofocus}
				variant={inputVariant}
				inputProps={{ maxLength: inputMaxLength }}
				TextFieldProps={{
					onChange: (e: React.ChangeEvent<HTMLInputElement>) => {
						if (isDocNumber && isBirthRegDocument) {
							e.target.value = e.target.value.toUpperCase();
						}

						props.onChange && props.onChange(props.field.name, e.target.value);
						(fieldState as FieldRenderProps<string, HTMLInputElement>).input.onChange(e);
					},
					onBlur: (e: React.FocusEvent<HTMLInputElement>) => {
						(fieldState as FieldRenderProps<string, HTMLInputElement>).input.onBlur(e);
					},
					error: !!error,
					helperText: error
				}}
				absoluteHintPosition={true}
				inputMode={inputMode}
				{...disableAutocompleteProps}
			/>
		);
	}

	if (props.field.input === InputType.Phone) {
		return (
			<PhoneInput
				className={props.className}
				value={fieldState.input.value}
				disabled={props.disabled}
				textFieldsProps={{
					label,
					variant: inputVariant,
					name: props.field.name,
					onChange: ((value: string) => {
						props.onChange && props.onChange(props.field.name, value);

						// Fucking `react-phone-number-input` changes the `onChange` handler signature
						// and passes in string value instead of ChangeEvent,
						// so we have to create our own event object.
						(fieldState as FieldRenderProps<string, HTMLInputElement>).input.onChange({
							...new Event('change'),
							target: {
								...fieldState.input,
								value: value
							}
						});
					}) as any,
					onBlur: (e: React.FocusEvent<HTMLInputElement>) => {
						(fieldState as FieldRenderProps<string, HTMLInputElement>).input.onBlur(e);
					},
					error: !!error,
					helperText: error
				}}
			/>
		);
	}

	if (props.field.type === TravellerFieldEnum.DocExpiryDate) {
		const value = formatInvalidPassengerDate(props.field.type, fieldState.input.value);

		const checkFieldIsRequired = useCallback(
			(allValues: object): boolean => {
				return fieldIsRequired(allValues['passengers'][props.passengerId], props.field.validations);
			},
			[props.field.validations]
		);

		return (
			<DocExpiryDate
				checkFieldIsRequired={checkFieldIsRequired}
				label={props.field.placeholder}
				value={value}
				name={props.field.name}
				className={props.className}
				onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
					props.onChange && props.onChange(props.field.name, e.target.value);
					(fieldState as FieldRenderProps<string, HTMLInputElement>).input.onChange(e);
				}}
				onBlur={(e: React.FocusEvent<HTMLInputElement>) => {
					(fieldState as FieldRenderProps<string, HTMLInputElement>).input.onBlur(e);
				}}
				error={error}
				absoluteHintPosition={true}
				disabled={props.disabled}
				variant={inputVariant}
			/>
		);
	}

	if (props.field.input === InputType.Date) {
		const value = formatInvalidPassengerDate(props.field.type as TravellerFieldEnum, fieldState.input.value);

		return (
			<DateField
				label={label}
				value={value}
				name={props.field.name}
				className={props.className}
				onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
					props.onChange && props.onChange(props.field.name, e.target.value);
					(fieldState as FieldRenderProps<string, HTMLInputElement>).input.onChange(e);
				}}
				onBlur={(e: React.FocusEvent<HTMLInputElement>) => {
					(fieldState as FieldRenderProps<string, HTMLInputElement>).input.onBlur(e);
				}}
				error={error}
				absoluteHintPosition={true}
				disabled={props.disabled}
				variant={inputVariant}
			/>
		);
	}

	if (props.field.input === InputType.Switch) {
		return (
			<PassengerSwitch
				value={fieldState.input.value ? fieldState.input.value[0] : ''}
				name={props.field.name}
				options={props.field.validations[0].options}
				className={props.className}
				onChange={value => {
					props.onChange && props.onChange(props.field.name, value);

					fieldState.input.onChange(value);
				}}
				onBlur={() => (fieldState as FieldRenderProps<string, HTMLInputElement>).input.onBlur()}
				error={error}
				disabled={props.disabled}
			/>
		);
	}
	if (props.field.input === InputType.Autocomplete) {
		return (
			<LastName
				{...props}
				onClear={dirty ? clearFieldsHandler : null}
				label={label}
				error={error}
				field={props.field as NameField}
				onFillFromDocument={onProfileDocumentSelectHandler}
				onChange={(value, event) => {
					props.onChange && props.onChange(props.field.name, value);

					(fieldState as FieldRenderProps<string, HTMLInputElement>).input.onChange(event);
				}}
				onBlur={() => (fieldState as FieldRenderProps<string, HTMLInputElement>).input.onBlur()}
				passengerId={props.passengerId}
				passengerType={props.passengerType}
				fieldState={fieldState}
				loadTravellers={props.loadTravellers}
				disabled={props.disabled}
				variant={inputVariant}
				onSwap={props.onSwap}
			/>
		);
	}

	if (props.field.input === InputType.Nationality) {
		return (
			<Nationality
				{...props}
				field={props.field}
				label={label}
				error={error}
				fieldState={fieldState}
				options={props.field.validations[0].options}
				className={props.className}
				onChange={(value, event) => {
					props.onChange && props.onChange(props.field.name, value);

					(fieldState as FieldRenderProps<string, HTMLInputElement>).input.onChange(event);
				}}
				onBlur={() => (fieldState as FieldRenderProps<string, HTMLInputElement>).input.onBlur()}
				disabled={props.disabled}
				variant={inputVariant}
			/>
		);
	}

	if (props.field.input === InputType.Select) {
		if (props.field.type === TravellerFieldEnum.DocType) {
			return (
				<DocumentType
					label={label}
					error={error}
					fieldState={fieldState}
					onChange={onChangeSelectHandler}
					variant={inputVariant}
					{...props}
				/>
			);
		}

		const isSubsidyField = props.field.type === AdditionalTravellerFieldEnum.Subsidy;
		const subsidyField = isSubsidyField ? (props.field as SubsidyField) : null;

		if (isSubsidyField && subsidyField?.options) {
			return (
				<PassengerSubsidySelect
					name={props.field.name}
					options={subsidyField.options}
					value={fieldState.input.value}
					onChange={(value, event) => {
						props.onChange?.(props.field.name, value as string);
						fieldState.input.onChange(event);
					}}
					onBlur={() => fieldState.input.onBlur()}
					disabled={props.disabled}
					error={error}
					className={props.className}
					multiple={false}
				/>
			);
		}

		const selectOptions =
			isSubsidyField && subsidyField?.options
				? subsidyField.options.map(option => ({
						key: option.key,
						value: option.label,
						disabled: option.disabled
				  }))
				: props.field.validations[0].options.map(option => ({
						key: option.value,
						value: option.label
				  }));

		return (
			<Select
				value={fieldState.input.value}
				options={selectOptions}
				variant={inputVariant}
				name={props.field.name}
				label={label}
				className={props.className}
				onChange={onChangeSelectHandler}
				onBlur={(fieldState as FieldRenderProps<string, HTMLTextAreaElement>).input.onBlur}
				error={error}
				disabled={props.disabled}
				translateValue={props.translateSelectOption}
				menuProps={props.menuProps}
				disableAutoSelect={isSubsidyField}
			/>
		);
	}

	return <></>;
});
